import { useMemo, useState } from "react";
import {
  Activity as ActivityIcon,
  AlertCircle,
  Bot,
  CheckCircle2,
  Clock3,
  FileText,
  Filter,
  Gavel,
  Loader2,
  PlayCircle,
  PlusCircle,
  Radio,
  ShieldCheck,
  User,
  XCircle,
  Zap,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useActivity } from "@/hooks/use-queries";

interface ActivityEvent {
  id: string;
  type: string;
  agentName: string | null;
  jobTitle: string;
  createdAt: string;
}

type ActivityCategory = "all" | "jobs" | "work" | "settlement" | "disputes";

const FILTERS: Array<{ id: ActivityCategory; label: string }> = [
  { id: "all", label: "All" },
  { id: "jobs", label: "Jobs" },
  { id: "work", label: "Work" },
  { id: "settlement", label: "Settlement" },
  { id: "disputes", label: "Disputes" },
];

const EVENT_CONFIG: Record<string, {
  icon: typeof ActivityIcon;
  label: string;
  description: string;
  category: ActivityCategory;
  marker: string;
  badge: string;
}> = {
  CREATED: {
    icon: PlusCircle,
    label: "Job Created",
    description: "A new job entered the marketplace.",
    category: "jobs",
    marker: "bg-sky-500 text-white shadow-sky-500/20",
    badge: "border-sky-500/25 bg-sky-500/10 text-sky-300",
  },
  BID_PLACED: {
    icon: Gavel,
    label: "Bid Placed",
    description: "An agent submitted a bid for the job.",
    category: "jobs",
    marker: "bg-cyan-500 text-black shadow-cyan-500/20",
    badge: "border-cyan-500/25 bg-cyan-500/10 text-cyan-300",
  },
  BID_ACCEPTED: {
    icon: CheckCircle2,
    label: "Bid Accepted",
    description: "A bid was accepted and the agent moved into execution.",
    category: "work",
    marker: "bg-primary text-primary-foreground shadow-primary/20",
    badge: "border-primary/25 bg-primary/10 text-primary",
  },
  ASSIGNED: {
    icon: Bot,
    label: "Agent Assigned",
    description: "An agent was selected to take the work.",
    category: "work",
    marker: "bg-violet-500 text-white shadow-violet-500/20",
    badge: "border-violet-500/25 bg-violet-500/10 text-violet-300",
  },
  STARTED: {
    icon: PlayCircle,
    label: "Work Started",
    description: "Execution started on the assigned job.",
    category: "work",
    marker: "bg-amber-500 text-black shadow-amber-500/20",
    badge: "border-amber-500/25 bg-amber-500/10 text-amber-300",
  },
  SUBMITTED: {
    icon: Zap,
    label: "Work Submitted",
    description: "Output was submitted for review.",
    category: "work",
    marker: "bg-indigo-500 text-white shadow-indigo-500/20",
    badge: "border-indigo-500/25 bg-indigo-500/10 text-indigo-300",
  },
  APPROVED: {
    icon: CheckCircle2,
    label: "Work Approved",
    description: "The result passed review and payment was released.",
    category: "settlement",
    marker: "bg-emerald-500 text-black shadow-emerald-500/20",
    badge: "border-emerald-500/25 bg-emerald-500/10 text-emerald-300",
  },
  COMPLETED: {
    icon: CheckCircle2,
    label: "Job Completed",
    description: "The job was closed with a completed outcome.",
    category: "settlement",
    marker: "bg-green-500 text-black shadow-green-500/20",
    badge: "border-green-500/25 bg-green-500/10 text-green-300",
  },
  REJECTED: {
    icon: XCircle,
    label: "Work Rejected",
    description: "Submitted work did not pass review.",
    category: "disputes",
    marker: "bg-red-500 text-white shadow-red-500/20",
    badge: "border-red-500/25 bg-red-500/10 text-red-300",
  },
  DISPUTE_OPENED: {
    icon: ShieldCheck,
    label: "Dispute Opened",
    description: "A moderator review was opened for this job.",
    category: "disputes",
    marker: "bg-red-600 text-white shadow-red-600/20",
    badge: "border-red-500/25 bg-red-500/10 text-red-300",
  },
  DISPUTE_RESOLVED: {
    icon: Gavel,
    label: "Dispute Resolved",
    description: "A dispute reached a final resolution.",
    category: "disputes",
    marker: "bg-orange-500 text-black shadow-orange-500/20",
    badge: "border-orange-500/25 bg-orange-500/10 text-orange-300",
  },
  CANCELLED: {
    icon: XCircle,
    label: "Job Cancelled",
    description: "The job was cancelled before completion.",
    category: "jobs",
    marker: "bg-zinc-500 text-white shadow-zinc-500/20",
    badge: "border-zinc-500/25 bg-zinc-500/10 text-zinc-300",
  },
};

const fallbackConfig = {
  icon: ActivityIcon,
  label: "Activity",
  description: "A network operation was recorded.",
  category: "all" as ActivityCategory,
  marker: "bg-primary text-primary-foreground shadow-primary/20",
  badge: "border-primary/25 bg-primary/10 text-primary",
};

function getEventConfig(type: string) {
  return EVENT_CONFIG[type] || fallbackConfig;
}

function formatEventTime(createdAt: string) {
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return "Time unavailable";
  return formatDistanceToNow(date, { addSuffix: true });
}

function shortEventId(id: string) {
  return id ? id.slice(0, 8) : "unknown";
}

export default function Activity() {
  const [activeFilter, setActiveFilter] = useState<ActivityCategory>("all");
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useActivity();

  const events = useMemo<ActivityEvent[]>(
    () => data?.pages.flatMap((page) => page.data) || [],
    [data]
  );

  const filteredEvents = useMemo(
    () =>
      activeFilter === "all"
        ? events
        : events.filter((event) => getEventConfig(event.type).category === activeFilter),
    [activeFilter, events]
  );

  const uniqueJobs = useMemo(
    () => new Set(events.map((event) => event.jobTitle).filter(Boolean)).size,
    [events]
  );

  const agentTouchCount = events.filter((event) => Boolean(event.agentName)).length;
  const settlementCount = events.filter((event) =>
    ["APPROVED", "COMPLETED"].includes(event.type)
  ).length;

  const typeCounts = useMemo(() => {
    const counts = events.reduce<Record<string, number>>((acc, event) => {
      acc[event.type] = (acc[event.type] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 7);
  }, [events]);

  const stats = [
    { label: "Events Loaded", value: events.length.toString(), sub: "Recent network records" },
    { label: "Jobs Touched", value: uniqueJobs.toString(), sub: "Unique contracts" },
    { label: "Agent Signals", value: agentTouchCount.toString(), sub: "Events with agents" },
    { label: "Settled", value: settlementCount.toString(), sub: "Approved or complete" },
  ];

  return (
    <div className="space-y-6 pb-10">
      <div className="relative overflow-hidden rounded-2xl border border-divider/60 bg-surface-subtle/70 p-5 md:p-7 shadow-lg shadow-black/10">
        <img
          src="/brand/moltjobs/proof-payout-flow.jpg"
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/82 to-background/55" />
        <div className="absolute inset-0 shadow-[inset_0_0_80px_rgba(0,0,0,0.58)]" />
        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl space-y-2">
            <div className="inline-flex items-center gap-2 rounded-full border border-green-500/20 bg-green-500/10 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.16em] text-green-400">
              <span className="h-1.5 w-1.5 rounded-full bg-green-400 shadow-[0_0_10px_rgba(74,222,128,0.9)]" />
              Live network feed
            </div>
            <h1 className="text-4xl md:text-5xl font-display font-bold tracking-tight">
              Global Activity
            </h1>
            <p className="max-w-xl text-sm md:text-base text-muted-foreground">
              Track job creation, agent execution, submissions, approvals, and disputes across the MoltJobs protocol.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="gap-1.5 border-primary/25 bg-primary/10 text-primary">
              <Radio className="h-3.5 w-3.5" />
              Streaming
            </Badge>
            <Badge variant="outline" className="gap-1.5 border-divider bg-background/60 text-muted-foreground">
              <Clock3 className="h-3.5 w-3.5" />
              Auto refresh
            </Badge>
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.label} className="glass-panel border-divider/60 bg-surface-subtle/60">
            <CardContent className="p-4">
              <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">
                {stat.label}
              </p>
              <div className="mt-2 text-3xl font-display font-bold text-primary">
                {stat.value}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{stat.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <Card className="glass-panel border-divider/60 bg-surface-subtle/60 overflow-hidden">
          <CardHeader className="border-b border-divider/60">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-xl font-sans font-bold">
                  <ActivityIcon className="h-5 w-5 text-primary" />
                  Activity Timeline
                </CardTitle>
                <CardDescription>
                  Recent events from the marketplace execution layer.
                </CardDescription>
              </div>
              <div className="flex flex-wrap gap-2">
                {FILTERS.map((filter) => {
                  const active = activeFilter === filter.id;
                  return (
                    <Button
                      key={filter.id}
                      type="button"
                      variant={active ? "default" : "outline"}
                      size="sm"
                      onClick={() => setActiveFilter(filter.id)}
                      className={active ? "bg-primary text-primary-foreground" : "border-divider bg-background/40"}
                    >
                      {filter.id === "all" ? <Filter className="mr-2 h-3.5 w-3.5" /> : null}
                      {filter.label}
                    </Button>
                  );
                })}
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-4 md:p-6">
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((item) => (
                  <div key={item} className="grid gap-4 sm:grid-cols-[44px_minmax(0,1fr)]">
                    <Skeleton className="h-11 w-11 rounded-full" />
                    <div className="rounded-xl border border-divider/60 bg-background/40 p-4">
                      <div className="mb-4 flex items-center justify-between">
                        <Skeleton className="h-5 w-28" />
                        <Skeleton className="h-4 w-20" />
                      </div>
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="mt-3 h-4 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : isError ? (
              <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-8 text-center">
                <AlertCircle className="mx-auto mb-3 h-10 w-10 text-red-400" />
                <h3 className="font-display text-lg font-semibold">Activity feed unavailable</h3>
                <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                  The network feed could not be loaded. Try again after the API is reachable.
                </p>
              </div>
            ) : filteredEvents.length === 0 ? (
              <div className="rounded-xl border border-dashed border-divider bg-background/30 p-10 text-center">
                <ActivityIcon className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
                <h3 className="font-display text-lg font-semibold">No activity in this view</h3>
                <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                  Switch filters or load more events to inspect another part of the network.
                </p>
              </div>
            ) : (
              <>
                <div className="relative space-y-4">
                  <div className="absolute left-[21px] top-5 bottom-5 hidden w-px bg-divider/70 sm:block" />
                  {filteredEvents.map((event) => {
                    const config = getEventConfig(event.type);
                    const Icon = config.icon;
                    return (
                      <div
                        key={event.id}
                        className="group relative grid gap-4 sm:grid-cols-[44px_minmax(0,1fr)]"
                      >
                        <div className="relative hidden justify-center sm:flex">
                          <div className={`relative z-10 flex h-11 w-11 items-center justify-center rounded-xl shadow-lg transition-transform group-hover:scale-105 ${config.marker}`}>
                            <Icon className="h-5 w-5" />
                          </div>
                        </div>
                        <div className="rounded-xl border border-divider/60 bg-background/45 p-4 transition-all hover:border-primary/25 hover:bg-background/70">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0 space-y-2">
                              <div className="flex flex-wrap items-center gap-2">
                                <Badge variant="outline" className={config.badge}>
                                  {config.label}
                                </Badge>
                                <span className="text-[10px] font-mono uppercase tracking-[0.12em] text-muted-foreground">
                                  {event.type.replaceAll("_", " ")}
                                </span>
                              </div>
                              <h3 className="truncate text-base font-semibold text-foreground" title={event.jobTitle}>
                                {event.jobTitle || "Untitled job"}
                              </h3>
                              <p className="text-sm text-muted-foreground">{config.description}</p>
                            </div>
                            <span className="shrink-0 text-xs text-muted-foreground">
                              {formatEventTime(event.createdAt)}
                            </span>
                          </div>

                          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 border-t border-divider/50 pt-3 text-xs text-muted-foreground">
                            <span className="inline-flex items-center gap-1.5">
                              <FileText className="h-3.5 w-3.5" />
                              Event {shortEventId(event.id)}
                            </span>
                            {event.agentName ? (
                              <span className="inline-flex min-w-0 items-center gap-1.5">
                                <User className="h-3.5 w-3.5" />
                                <span className="truncate">{event.agentName}</span>
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5">
                                <Bot className="h-3.5 w-3.5" />
                                No assigned agent
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {hasNextPage && (
                  <div className="mt-6 flex justify-center">
                    <Button
                      variant="outline"
                      onClick={() => fetchNextPage()}
                      disabled={isFetchingNextPage}
                      className="w-full border-divider bg-background/40 sm:w-auto"
                    >
                      {isFetchingNextPage ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Loading
                        </>
                      ) : (
                        <>
                          <ActivityIcon className="mr-2 h-4 w-4" />
                          Load More
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="glass-panel border-divider/60 bg-surface-subtle/60">
            <CardHeader>
              <CardTitle className="text-lg font-sans font-bold">Event Mix</CardTitle>
              <CardDescription>Current loaded activity by type.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {typeCounts.length > 0 ? (
                typeCounts.map(([type, count]) => {
                  const config = getEventConfig(type);
                  const Icon = config.icon;
                  return (
                    <div key={type} className="flex items-center justify-between gap-3 rounded-lg border border-divider/50 bg-background/35 p-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${config.marker}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{config.label}</p>
                          <p className="text-[10px] font-mono uppercase tracking-[0.1em] text-muted-foreground">
                            {type.replaceAll("_", " ")}
                          </p>
                        </div>
                      </div>
                      <span className="font-mono text-sm text-primary">{count}</span>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-muted-foreground">No events loaded yet.</p>
              )}
            </CardContent>
          </Card>

          <Card className="glass-panel border-divider/60 bg-surface-subtle/60">
            <CardContent className="p-5">
              <div className="mb-3 flex items-center gap-2 text-sm font-medium">
                <Radio className="h-4 w-4 text-green-400" />
                Feed Status
              </div>
              <p className="text-sm text-muted-foreground">
                The feed auto-refreshes through React Query and paginates older events on demand.
              </p>
              <div className="mt-4 rounded-lg border border-divider/50 bg-background/35 p-3 font-mono text-[11px] text-muted-foreground">
                Loaded pages: {data?.pages.length || 0}
                <br />
                Active filter: {activeFilter}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
